import { FunctionComponent, useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { IntlShape } from 'react-intl';
import { List, MapPin, CurrencyDollar, Buildings, Briefcase } from '@phosphor-icons/react';
import { reactGaEvent } from '../../utils/cookie-consent/react-ga-safe';
import { usePageContext } from 'vike-react/usePageContext';
import { AutocompleteHandle } from '../Autocomplete';
import Avatar from '../Avatar';
import { LazyTooltip } from '../Tooltip';
import { SearchNavbar } from './SearchNavbar';
import { FilterCategoryButtons } from '../filters/FilterCategoryButtons';
import { TopFilters } from '../filters/TopFilters';
import { useSearchFilters, useSearchQuery } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { useEffectiveUserProfilePhoto } from '../../hooks/useEffectiveUserProfilePhoto';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';
import { getLocaleFromPathname, normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import {
  CORE_CONTROL_GHOST,
  CORE_HEADER_STICKY_SRP,
  CORE_MENU_ITEM,
  CORE_MENU_ITEM_ACTIVE,
  CORE_MENU_SURFACE,
  CORE_NAV_ROW_BORDER,
  FILTER_CAT_COMPANY,
  FILTER_CAT_JOB,
  FILTER_CAT_LOCATION,
  FILTER_CAT_SALARY,
} from '../../constants/corePublicTheme';

interface DesktopFilterCategoriesProps {
  intl: IntlShape;
  activeFilterModal: string | null;
  setActiveFilterModal: (filter: string | null) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isAllFiltersModalOpen?: boolean;
  setIsAllFiltersModalOpen?: (open: boolean) => void;
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  user?: any;
  closeAutocomplete?: () => void;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
}

const DesktopFilterCategories: FunctionComponent<DesktopFilterCategoriesProps> = ({
  intl,
  activeFilterModal,
  setActiveFilterModal,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isAllFiltersModalOpen,
  setIsAllFiltersModalOpen,
  clearSearch,
  user,
  closeAutocomplete,
  autocompleteRef,
  onAutocompleteOpen,
}) => {
  const { filters } = useSearchFilters();
  const menuAvatarSrc = useEffectiveUserProfilePhoto(user ?? null);
  const pageContext = usePageContext();
  const pathname = (pageContext as any)?.urlPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  const locale = normalizeSupportedLocale(pageContext?.locale ?? intl?.locale ?? getLocaleFromPathname(pathname) ?? 'en');
  
  // State for language selector dropdown (xl screens and above)
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  
  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLanguageMenuOpen]);
  
  // Facet computation is now handled by TopFilters component
  const { query } = useSearchQuery();
  const filtersObj = (typeof filters === 'object' && filters) ? filters as Record<string, string[]> : {};
  const { facetsData } = useFacets();

  // Get location items for filtering
  const locationItems = facetsData.location || [];
  
  // Filter location facets to only show Finland, Remote, or Global locations
  // Always include active (selected) filters even if they don't match keywords
  const filterLocationFacets = useCallback((items: Array<{ value: string; label: string; count: number }>) => {
    const allowedKeywords = [
      // Finland
      'finland', 'suomi', 'finländ',
      // Remote
      'remote', 'etätyö', 'etä', 'distans', 'fjärr',
      // Global
      'global', 'maailmanlaajuinen', 'världsomfattande',
    ];
    
    // Exclude "Remote - United States" and its translations
    const excludedPatterns = [
      'remote - united states',
      'etätyö - yhdysvallat',
      'etä - yhdysvallat',
      'distans - usa',
      'fjärr - usa',
      'distans - förenta staterna',
      'fjärr - förenta staterna',
    ];
    
    const activeLocationValues = filtersObj.location || [];
    
    return items.filter(item => {
      // Always include if it's an active filter
      if (activeLocationValues.includes(item.value)) {
        return true;
      }
      
      const lowerValue = item.value.toLowerCase();
      const lowerLabel = item.label.toLowerCase();
      
      // Exclude "Remote - United States" and its translations
      if (excludedPatterns.some(pattern => 
        lowerValue.includes(pattern) || lowerLabel.includes(pattern)
      )) {
        return false;
      }
      
      // Check if value or label contains any of the allowed keywords
      return allowedKeywords.some(keyword => 
        lowerValue.includes(keyword) || lowerLabel.includes(keyword)
      );
    });
  }, [filtersObj.location]);
  
  // Memoize filtered location items
  const filteredLocationItems = useMemo(() => filterLocationFacets(locationItems), [filterLocationFacets, locationItems]);
  
  // Category buttons (top row)
  type FilterCategory = {
    id: string;
    label: string;
    icon?: React.ReactNode;
    colorClass: string;
    hasFilters: boolean;
  };
  
  const handleCategoryClick = useCallback((categoryId: string) => {
    // Close autocomplete when filter button is clicked
    closeAutocomplete?.();
    
    if (activeFilterModal === categoryId) {
      setActiveFilterModal(null);
    } else {
      setActiveFilterModal(categoryId);
    }
  }, [activeFilterModal, setActiveFilterModal, closeAutocomplete]);
  
  const categories: FilterCategory[] = [
    {
      id: 'location',
      label: intl.formatMessage({ id: 'search.refinements.location', defaultMessage: 'Location', description: 'Filter label for location selection in search refinements' }),
      icon: <MapPin weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_LOCATION,
      hasFilters: !!(filtersObj.location?.length || filtersObj.remoteness?.length),
    },
    {
      id: 'salary',
      label: intl.formatMessage({ id: 'search.refinements.salary', defaultMessage: 'Salary', description: 'Filter label for salary range selection in search refinements' }),
      icon: <CurrencyDollar weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_SALARY,
      hasFilters: !!(filtersObj.salaryRangeMin?.length || filtersObj.vacationWeeks?.length),
    },
    {
      id: 'company',
      label: intl.formatMessage({ id: 'search.refinements.company', defaultMessage: 'Company', description: 'Filter label for company selection in search refinements' }),
      icon: <Buildings weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_COMPANY,
      hasFilters: !!(filtersObj.companyId?.length || filtersObj.companyIndustry?.length),
    },
    {
      id: 'job',
      label: intl.formatMessage({ id: 'search.refinements.job', defaultMessage: 'Job', description: 'Filter label for job-related filters (type, title, etc.) in search refinements' }),
      icon: <Briefcase weight="fill" className="w-4 h-4" />,
      colorClass: FILTER_CAT_JOB,
      hasFilters: !!(
        filtersObj.jobType?.length || 
        filtersObj.normalisedRoleLevel?.length || 
        filtersObj.requiredLanguages?.length || 
        filtersObj.benefits?.length || 
        filtersObj.skillsAndTechnologies?.length ||
        filtersObj.field?.length ||
        filtersObj.educationLevel?.length ||
        filtersObj.peopleManagementTier?.length ||
        filtersObj.lastSeenMin?.length ||
        filtersObj.excludeSaved?.length ||
        filtersObj.excludeViewed?.length ||
        filtersObj.excludeApplied?.length
      ),
    },
  ];

  return (
    <header className={CORE_HEADER_STICKY_SRP} role="banner">
      {/* Top row: Logo + Searchbar + Filter Categories + Hamburger Menu (lg and above) */}
      <nav className={`hidden lg:flex items-center gap-3 h-14 min-h-14 py-3 px-4 xl:px-20 md:px-10 ${CORE_NAV_ROW_BORDER}`} aria-label="Main navigation">
        {/* Left side: Logo + Searchbar */}
        <SearchNavbar
          intl={intl}
          clearSearch={clearSearch}
          autocompleteRef={autocompleteRef}
          onAutocompleteOpen={onAutocompleteOpen}
        />
        
        {/* Center: Filter Category buttons */}
        <FilterCategoryButtons
          intl={intl}
          categories={categories}
          activeCategory={activeFilterModal}
          onCategoryClick={handleCategoryClick}
          className="flex-1 justify-center"
        />
        
        {/* Right side: Language Selector (xl screens and above) and Hamburger Menu */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          {/* Language Selector - only visible on xl screens and above */}
          <div ref={languageMenuRef} className="hidden xl:block relative cursor-pointer">
            <LazyTooltip
              content={intl.formatMessage({ id: 'tooltip.language', defaultMessage: 'Change website language (English, Finnish, Swedish)', description: 'Tooltip text for language selector button' })}
              position="bottom"
            >
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg cursor-pointer ${CORE_CONTROL_GHOST}`}
                aria-label={intl.formatMessage({ id: 'navigation.language', defaultMessage: 'Language', description: 'Button aria-label for language selector in navigation' })}
              >
                {locale === 'fi' && <span className="text-xs">FI</span>}
                {locale === 'sv' && <span className="text-xs">SV</span>}
                {locale === 'en' && <span className="text-xs">EN</span>}
              </button>
            </LazyTooltip>
            {isLanguageMenuOpen && (
              <div className={`absolute top-full right-0 mt-2 w-40 py-2 z-50 ${CORE_MENU_SURFACE}`}>
                <button
                  onClick={() => {
                    reactGaEvent({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'fi',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'fi', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'fi' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
                  }`}
                >
                  <span>FI</span>
                </button>
                <button
                  onClick={() => {
                    reactGaEvent({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'sv',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'sv', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'sv' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
                  }`}
                >
                  <span>SV</span>
                </button>
                <button
                  onClick={() => {
                    reactGaEvent({
                      category: 'interactions',
                      action: 'click_change_language',
                      label: 'en',
                      nonInteraction: false,
                    });
                    window.location.href = convertUrlToLanguage(window.location.pathname, 'en', intl);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
                    locale === 'en' ? CORE_MENU_ITEM_ACTIVE : CORE_MENU_ITEM
                  }`}
                >
                  <span>EN</span>
                </button>
              </div>
            )}
          </div>
          <button
            className={`flex items-center gap-2 py-1.5 rounded-full cursor-pointer ${CORE_CONTROL_GHOST} ${
              user && menuAvatarSrc ? 'pl-1 pr-2' : 'px-2'
            }`}
            onClick={() => {
              const willOpen = !(isMobileMenuOpen ?? false);
              setIsMobileMenuOpen?.(willOpen);
              // Close All Filters modal if opening mobile menu
              if (willOpen && isAllFiltersModalOpen) {
                setIsAllFiltersModalOpen?.(false);
              }
            }}
            aria-label={intl.formatMessage({
              id: 'navigation.menu',
              defaultMessage: 'Menu',
            })}
          >
            {user && menuAvatarSrc ? (
              <>
                <div className="flex-shrink-0 overflow-hidden rounded-full">
                  <Avatar
                    src={menuAvatarSrc}
                    size={32}
                    name={user.name || 'User'}
                  />
                </div>
                <List className="w-5 h-5 text-[#3D3529] dark:text-[#C4A882] flex-shrink-0" />
              </>
            ) : (
              <List className="w-6 h-6 text-[#3D3529] dark:text-[#C4A882]" />
            )}
          </button>
        </div>
      </nav>
      
      {/* Second row: Individual facet values (virtualized horizontally) */}
      <TopFilters
        intl={intl}
        clearSearch={clearSearch}
        facetsData={{
          ...facetsData,
          location: filteredLocationItems, // Use filtered location items
        }}
        filtersObj={filtersObj}
        query={query}
      />
    </header>
  );
};

// Memoize DesktopFilterCategories to prevent unnecessary re-renders
export const DesktopFilterCategoriesMemo = memo(DesktopFilterCategories);
